import apiClient from './client';

export interface Transaction {
	chain: string;
	txHash: string;
	fromAddress: string;
	toAddress: string | null;
	value: string;
	tokenAddress: string | null;
	tokenSymbol: string | null;
	tokenDecimals: number | null;
	blockNumber: number;
	blockTimestamp: string;
	status: string;
}

// Transaction from Analytics Engine (ledger)
export interface TransactionRecord {
	id: number;
	userId: string;
	txHash: string;
	fromAddress: string;
	toAddress: string | null;
	tokenSymbol: string;
	amount: string;
	txType: string;
	chain: string;
	timestamp: string;
	blockNumber: number;
	feeValue?: string;
	gasEth?: number | null;
	gasUsd?: number | null;
	isMevSuspected?: boolean;
	estimatedMevLossUsd?: number | null;
	category?: string | null;
	fiatValueUsd?: number | null;
	enrichedAt?: string | null;
	incomeType?: 'staking_reward' | 'airdrop' | 'gift' | 'fork' | 'mining' | 'interest' | null;
	incomeValueUsd?: number | null;
	sourceLabel?: string | null;
	importedAt?: string | null;
	reconciliationJobId?: string | null;
	isReconciliationMatch?: boolean;
}

export interface TransactionsReconciliationSummary {
	sourceLabel?: string | null;
	importedAt?: string | null;
	imported?: number | null;
	skipped?: number | null;
	errors?: number | null;
	matchedCount?: number | null;
	exchangeProvenance?: TransactionsExchangeProvenance | null;
}

export interface TransactionsExchangeLastSuccessfulSyncProof {
	available: boolean;
	syncedAt: string | null;
	freshnessBucket: 'fresh' | 'stale' | 'never_synced';
	staleHours: number | null;
	proofSource: 'exchange_api_connections';
}

export interface TransactionsExchangeSafeRetryGuidance {
	code: string;
	retryableNow: boolean;
	retryAction: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
	dominantAction: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
}

export interface TransactionsExchangeRouteGuidanceEntry {
	routeRole: 'proof_owner' | 'management_owner' | 'summary_only' | 'ledger_only';
	handoffRoute: '/exchange-sync' | null;
	primaryNextStep: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
	dominantAction: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
	safeRetryGuidance: TransactionsExchangeSafeRetryGuidance;
}

export interface TransactionsExchangeProvenance {
	exchange: string;
	sourceLabel: string;
	apiConnectionId: string | null;
	lastVerifiedSyncAt: string | null;
	freshnessState: 'healthy' | 'stale_verified' | 'never_synced';
	freshnessBucket: 'fresh' | 'stale' | 'never_synced';
	freshnessStaleHours: number | null;
	healthState: 'healthy' | 'stale' | 'blocked' | 'never_synced';
	healthReason:
		| 'recent_successful_sync'
		| 'stale_sync'
		| 'sync_error'
		| 'never_synced'
		| 'provider_credentials_required'
		| 'provider_auth_required'
		| 'provider_replay_blocked';
	blockerClassification: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
	blockerCode: 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
	proofSource: 'exchange_api_connections';
	primaryNextStep: 'sync_now' | 'retry_sync' | 'manage_connection' | null;
	dominantAction: 'review_last_successful_sync' | 'sync_now' | 'retry_sync' | 'manage_connection' | null;
	lastSuccessfulSyncProof: TransactionsExchangeLastSuccessfulSyncProof;
	credentialActionRequired: boolean;
	providerAuthActionRequired: boolean;
	providerReplayBlocked: boolean;
	safeRetryGuidance: TransactionsExchangeSafeRetryGuidance;
	proofAuthority?: 'mounted_last_successful_sync' | null;
	providerReplayScope?: 'management_owned_path' | null;
	routeGuidance?: {
		import: TransactionsExchangeRouteGuidanceEntry;
		management: TransactionsExchangeRouteGuidanceEntry;
		summary: TransactionsExchangeRouteGuidanceEntry;
		ledger: TransactionsExchangeRouteGuidanceEntry;
	} | null;
}

export interface TransactionsResponse {
	transactions: TransactionRecord[];
	total: number;
	page: number;
	limit: number;
	reconciliation?: TransactionsReconciliationSummary | null;
}

export interface TransactionSummary {
	total: number;
	earliest: string | null;
	latest: string | null;
}

export interface EnrichmentStats {
	totalTransactions: number;
	enrichedCount: number;
	enrichmentPercentage: number;
}

export async function getTransactionsEthereum(address: string, limit: number = 50) {
	const res = await apiClient.get<{ chain: string; address: string; transactions: Transaction[]; count: number }>(
		`/blockchain/transactions/ethereum/${address}?limit=${limit}`
	);
	return res.data;
}

/**
 * Get transaction ledger for user from Analytics Engine
 * v1: Returns transactions ingested from blockchain
 */
export async function getTransactions(
	userId: string,
	page: number = 0,
	limit: number = 100
): Promise<TransactionsResponse> {
	const res = await apiClient.get<TransactionsResponse>(
		`/transactions/${userId}?page=${page}&limit=${limit}`
	);
	return res.data;
}

/**
 * Get transaction summary (count, date range)
 */
export async function getTransactionsSummary(userId: string): Promise<TransactionSummary> {
	const res = await apiClient.get<TransactionSummary>(`/transactions/${userId}/summary`);
	return res.data;
}

/**
 * Trigger transaction enrichment for user
 */
export async function enrichTransactions(userId: string): Promise<{ message: string; stats: EnrichmentStats }> {
	const res = await apiClient.post<{ message: string; stats: EnrichmentStats }>(
		`/transactions/${userId}/enrich`,
		{}
	);
	return res.data;
}

/**
 * Get enrichment statistics for user
 */
export async function getEnrichmentStats(userId: string): Promise<EnrichmentStats> {
	const res = await apiClient.get<EnrichmentStats>(`/transactions/${userId}/enrich-stats`);
	return res.data;
}

// ==================== Lot Tracking Types & Functions (Chunk 31) ====================

export interface TaxLot {
	id: number;
	userId: string;
	symbol: string;
	amount: number;
	costBasisPerUnit: number;
	dateAcquired: string;
	method: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';
	createdAt: string;
	updatedAt: string;
}

export interface LotAssignment {
	id: number;
	transactionId: number;
	lotId: number;
	allocatedAmount: number;
	allocatedPercentage: number | null;
	createdAt: string;
}

export interface TransactionDetail extends TransactionRecord {
	lots: LotAssignment[];
}

export interface TransactionDetailResponse {
	transaction: TransactionDetail;
	lots?: LotAssignment[];
}

export interface CreateLotParams {
	symbol: string;
	amount: number;
	costBasisPerUnit: number;
	dateAcquired: string;
	method?: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';
}

export interface UpdateLotParams {
	amount?: number;
	costBasisPerUnit?: number;
	method?: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';
}

export interface AssignLotParams {
	lotId: number;
	allocatedAmount: number;
	allocatedPercentage?: number;
}

export interface TransactionSearchParams {
	/** Full-text search: ILIKE match on symbol, address fields, tx_hash */
	q?: string;
	/** Multi-select transaction types (e.g. SWAP, STAKE, BRIDGE) */
	types?: string[];
	/** Multi-select chains */
	chains?: string[];
	/** ISO 8601 start date */
	from_date?: string;
	/** ISO 8601 end date */
	to_date?: string;
	/** Minimum fiat value in USD */
	min_value_usd?: number;
	/** Maximum fiat value in USD */
	max_value_usd?: number;
	sort?: 'date_desc' | 'date_asc' | 'value_desc' | 'value_asc';
	page?: number;
	limit?: number;
	reconciliation_source?: 'csv_import' | 'exchange_sync';
	reconciliation_job_id?: string;
	reconciliation_imported_after?: string;
	reconciliation_exchange?: string;
	reconciliation_only?: boolean;
	// Legacy fields kept for backward compatibility
	symbol?: string;
	chain?: string;
	fromDate?: string;
	toDate?: string;
	minAmount?: number;
	maxAmount?: number;
	txType?: string;
	category?: string;
}

/**
 * Get all tax lots for a user
 */
export async function getLots(userId: string, symbol?: string): Promise<{ lots: TaxLot[] }> {
	const params = symbol ? `?symbol=${symbol}` : '';
	const res = await apiClient.get<{ lots: TaxLot[] }>(`/transactions/${userId}/lots${params}`);
	return res.data;
}

/**
 * Create a new tax lot
 */
export async function createLot(userId: string, params: CreateLotParams): Promise<{ lot: TaxLot }> {
	const res = await apiClient.post<{ lot: TaxLot }>(`/transactions/${userId}/lots`, params);
	return res.data;
}

/**
 * Update an existing tax lot
 */
export async function updateLot(userId: string, lotId: number, params: UpdateLotParams): Promise<{ lot: TaxLot }> {
	const res = await apiClient.put<{ lot: TaxLot }>(`/transactions/${userId}/lots/${lotId}`, params);
	return res.data;
}

/**
 * Delete a tax lot
 */
export async function deleteLot(userId: string, lotId: number): Promise<{ success: boolean; message: string }> {
	const res = await apiClient.delete<{ success: boolean; message: string }>(`/transactions/${userId}/lots/${lotId}`);
	return res.data;
}

/**
 * Get transaction detail with lot assignments
 */
export async function getTransactionDetail(userId: string, txId: number): Promise<TransactionDetailResponse> {
	const res = await apiClient.get<TransactionDetailResponse>(`/transactions/${userId}/${txId}`);
	return res.data;
}

/**
 * Assign a transaction lot on the existing transactions route family.
 */
export async function assignLot(
	userId: string,
	transactionId: number,
	params: AssignLotParams,
): Promise<LotAssignment> {
	const res = await apiClient.post<LotAssignment>(`/transactions/${userId}/${transactionId}/assign-lot`, params);
	return res.data;
}

/**
 * Search/filter transactions with advanced criteria.
 * Calls GET /transactions/:userId with composable query params.
 */
export async function searchTransactions(userId: string, params: TransactionSearchParams): Promise<TransactionsResponse> {
	const queryParams = new URLSearchParams();
	if (params.q) queryParams.append('q', params.q);
	if (params.types && params.types.length > 0) queryParams.append('type', params.types.join(','));
	if (params.chains && params.chains.length > 0) queryParams.append('chain', params.chains.join(','));
	if (params.from_date) queryParams.append('from_date', params.from_date);
	if (params.to_date) queryParams.append('to_date', params.to_date);
	if (params.min_value_usd !== undefined) queryParams.append('min_value_usd', params.min_value_usd.toString());
	if (params.max_value_usd !== undefined) queryParams.append('max_value_usd', params.max_value_usd.toString());
	if (params.sort) queryParams.append('sort', params.sort);
	if (params.page !== undefined) queryParams.append('page', params.page.toString());
	if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
	if (params.reconciliation_source) queryParams.append('reconciliation_source', params.reconciliation_source);
	if (params.reconciliation_job_id) queryParams.append('reconciliation_job_id', params.reconciliation_job_id);
	if (params.reconciliation_imported_after) queryParams.append('reconciliation_imported_after', params.reconciliation_imported_after);
	if (params.reconciliation_exchange) queryParams.append('reconciliation_exchange', params.reconciliation_exchange);
	if (params.reconciliation_only) queryParams.append('reconciliation_only', '1');

	// Legacy single-value fallbacks
	if (!params.chains?.length && params.chain) queryParams.append('chain', params.chain);
	if (params.fromDate) queryParams.append('from_date', params.fromDate);
	if (params.toDate) queryParams.append('to_date', params.toDate);

	const res = await apiClient.get<TransactionsResponse>(`/transactions/${userId}?${queryParams.toString()}`);
	return res.data;
}

// ==================== Tax Report Types & Functions (Chunk 31) ====================

export interface RealizedGain {
	transactionId: number;
	symbol: string;
	saleDate: string;
	saleAmount: number;
	salePrice: number;
	totalProceeds: number;
	costBasis: number;
	realizedGain: number;
	holdingPeriod: 'SHORT_TERM' | 'LONG_TERM';
	lotsUsed: {
		lotId: number;
		amount: number;
		costBasis: number;
		dateAcquired: string;
	}[];
}

export interface TaxReportSummary {
	totalRealizedGain: number;
	totalRealizedLoss: number;
	netGain: number;
	transactionCount: number;
	shortTermGain: number;
	longTermGain: number;
	totalOrdinaryIncome: number;
}

export interface IncomeEvent {
	transactionId: number;
	symbol: string;
	date: string;
	incomeType: 'staking_reward' | 'airdrop' | 'gift' | 'fork' | 'mining' | 'interest';
	amount: number;
	incomeValueUsd: number;
}

export interface TaxReport {
	id?: number;
	userId: string;
	method: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';
	yearFrom: number;
	yearTo: number;
	summary: TaxReportSummary;
	gains: RealizedGain[];
	incomeEvents: IncomeEvent[];
	generatedAt: string;
}

export interface GenerateTaxReportParams {
	method: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';
	yearFrom: number;
	yearTo: number;
}

/**
 * Generate a new tax report
 */
export async function generateTaxReport(userId: string, params: GenerateTaxReportParams): Promise<{ report: TaxReport }> {
	const res = await apiClient.post<{ report: TaxReport }>(`/transactions/${userId}/tax-reports/generate`, params);
	return res.data;
}

/**
 * Get all tax reports for a user
 */
export async function getTaxReports(userId: string, limit: number = 10, offset: number = 0): Promise<{ reports: TaxReport[] }> {
	const res = await apiClient.get<{ reports: TaxReport[] }>(`/transactions/${userId}/tax-reports?limit=${limit}&offset=${offset}`);
	return res.data;
}

/**
 * Get a specific tax report by ID
 */
export async function getTaxReportDetail(userId: string, reportId: number): Promise<{ report: TaxReport }> {
	const res = await apiClient.get<{ report: TaxReport }>(`/transactions/${userId}/tax-reports/${reportId}`);
	return res.data;
}

/**
 * Export a tax report as CSV or JSON
 */
export async function exportTaxReport(userId: string, reportId: number, format: 'csv' | 'json'): Promise<Blob> {
	const res = await apiClient.get(`/transactions/${userId}/tax-reports/${reportId}/export?format=${format}`, {
		responseType: 'blob',
	});
	return res.data;
}

// ==================== Income-Type Labelling (Chunk 78) ====================

export type IncomeTypeValue = 'staking_reward' | 'airdrop' | 'gift' | 'fork' | 'mining' | 'interest' | null;

export interface IncomeEventsResponse {
	events: IncomeEvent[];
	byType: Record<string, { count: number; totalUsd: number }>;
	totalOrdinaryIncome: number;
}

/**
 * Set or clear the income type and value for a transaction
 */
export async function setTransactionIncomeType(
	txId: number,
	incomeType: IncomeTypeValue,
	incomeValueUsd?: number | null,
): Promise<{ transaction: { id: number; incomeType: IncomeTypeValue; incomeValueUsd: number | null } }> {
	const res = await apiClient.patch(`/transactions/${txId}/income-type`, { incomeType, incomeValueUsd });
	return res.data;
}

/**
 * Get income events (staking rewards, airdrops, etc.) for a user, optionally filtered by year
 */
export async function getIncomeEvents(
	userId: string,
	yearFrom?: number,
	yearTo?: number,
): Promise<IncomeEventsResponse> {
	const params = new URLSearchParams();
	if (yearFrom !== undefined) params.append('yearFrom', yearFrom.toString());
	if (yearTo !== undefined) params.append('yearTo', yearTo.toString());
	const query = params.toString() ? `?${params.toString()}` : '';
	const res = await apiClient.get<IncomeEventsResponse>(`/transactions/${userId}/income-events${query}`);
	return res.data;
}
